import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useAppDispatch, useAppSelector } from '../../lib/redux/hook'
import { ArrowRightIcon } from '../../ui/icons/arrowRightIcon'
import { DashboardIcon } from '../../ui/icons/dashboardIcon'
import { LayoutComponent } from '../../ui/layout'
import { BillowButton } from '../../ui/molecules/billowButton'
import { SelectComponent } from '../../ui/molecules/select'
import { SpinnerComponent } from '../../ui/molecules/spinner'
import { ConfirmModal } from '../../ui/organisms/confirmModal'
import { ErrorPopup } from '../../ui/organisms/errorPopup'
import { ModalComponent } from '../../ui/organisms/modal'
import { MenuItems } from '../../ui/organisms/sidebar/dto'
import { SuccessPopup } from '../../ui/organisms/successPopup'
import { PaymentDTO } from '../customer/payment/dto'
import { createPaymentWithId, findAllPayments, setCreatePaymentStatus } from '../customer/payment/slice'
import { findAllCustomers } from '../customer/slice'

export default function DashboardAdmin() {

  const [selectedCustomer, setSelectedCustomer] = useState<string>()
  const [modalOpen, setModalOpen] = useState(false)
  const [namespace, setNamespace] = useState('')

  const monthNames = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  function aggregatePaymentsByMonth(payments: PaymentDTO[]) {
    const monthlyTotals = new Array(12).fill(0);

    for (const payment of payments) {
      const monthIndex = new Date(payment.createdAt).getMonth()
      monthlyTotals[monthIndex] += payment.amount;
    }

    const data = monthlyTotals.map((total, index) => ({
      month: monthNames[index],
      "Totale ricavi": total,
      amt: total,
    }));

    return data;
  }

  function aggregateRealCostsByMonth(payments: PaymentDTO[]) {

    const monthlyCosts = new Array(12).fill(0);

    for (const payment of payments) {
      const monthIndex = new Date(payment.createdAt).getMonth();
      const realCost = payment.amount - payment.markup_rate_applied;
      monthlyCosts[monthIndex] += realCost;
    }

    const data = monthlyCosts.map((total, index) => ({
      month: monthNames[index],
      "Costo reale": Math.round(total),
    }));

    return data;
  }

  const customerState = useAppSelector(state => state.customer)
  const paymentState = useAppSelector(state => state.payment)
  const customerOptions = customerState.findAllCustomersResponse ? customerState.findAllCustomersResponse!.customers.filter(item => item.vat.trim() && item.active).map(customer => ({
    label: customer.business_name,
    value: customer.id
  })) : []

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const payments = aggregatePaymentsByMonth(paymentState.findAllPaymentResponse?.payments ?? [])
  const paymentsByCustomer = aggregatePaymentsByMonth(paymentState.findAllPaymentResponse?.payments.filter(item => item.customer_id.toString() === selectedCustomer?.toString()) ?? [])
  const realCosts = aggregateRealCostsByMonth(paymentState.findAllPaymentResponse?.payments ?? [])

  function findPaymentCustomer(payment: PaymentDTO) {
    if (!customerState.findAllCustomersResponse) return undefined
    const customer = customerState.findAllCustomersResponse.customers.find(item => item.id.toString() === payment.customer_id.toString())
    if (customer)
      return customer.business_name
    else return 'Cliente eliminato'
  }

  useEffect(() => {
    dispatch(findAllPayments({ ...paymentState.findAllFilters, sortBy: 'created_at', sort: -1 }))
    dispatch(findAllCustomers(customerState.customerFilters))
  }, [])

  useEffect(() => {
    if (paymentState.createPaymentWithIdStatus === 'successfully') {
      dispatch(findAllPayments({ ...paymentState.findAllFilters, sortBy: 'created_at', sort: -1 }))
    }
  }, [paymentState.createPaymentWithIdStatus])

  const [confirm, setConfirm] = useState(false)

  return (
    <LayoutComponent menuItem={MenuItems.DASHBOARD} headingLabel={'Dashboard'} headingButtons={[
      <BillowButton onClick={() => setModalOpen(true)} label='Richiedi pagamento' iconPosition={'off'} variant={'solid'}
      />]}
      breadcrumbItems={['Dashboard']} breadcrumbIcon={<DashboardIcon size={'20'} color={'#6237FB'} />}>
      <ConfirmModal open={confirm} handleClose={() => setConfirm(false)} title={"Creazione pagamento"}
        description={"Vuoi davvero creare un pagamento?"} labelDeny={"No"} labelConfirm={"Si"} actionConfirm={
          () => {
            dispatch(createPaymentWithId(namespace))
            setNamespace('')
            setConfirm(false)
          }
        } />
      <SuccessPopup message='Pagamento creato con successo!' active={paymentState.createPaymentWithIdStatus === 'successfully'} close={() => dispatch(setCreatePaymentStatus('idle'))} />
      <ErrorPopup message='Creazione pagamento fallita!' active={paymentState.createPaymentWithIdStatus === 'failed'} close={() => dispatch(setCreatePaymentStatus('idle'))} />
      <div className=' flex gap-3 flex-col'>
        {paymentState.findAllPaymentResponse && <div className=' rounded-xl shadow-xl border border-[#6237FB] p-4 flex flex-col items-start h-[230px] justify-between'>
          <div className="flex flex-row justify-between items-center pt-1">
            <span className="text-[#170264] font-bold leading-[18px] text-lg">Ultimi pagamenti</span>
          </div>
          <div className=' w-full'>
            {paymentState.findAllPaymentStatus === 'loading' ?
              <div className=" flex items-center justify-center">
                <SpinnerComponent />
              </div> :
              paymentState.findAllPaymentResponse?.payments.length > 0 ?
                paymentState.findAllPaymentResponse?.payments.slice(0, 4).map(item =>
                  <div onClick={() => navigate('/customer/' + item.customer_id)} key={item.createdAt.toString()} className="hover:bg-neutral-100 rounded-lg cursor-pointer items-center flex flex-row justify-between border-b border-brandPrimary-100 py-[6px] px-2 gap-4 w-full">
                    <span className=" text-brandSecondary-500 font-medium text-[14px] leading-[18px]">
                      {findPaymentCustomer(item)}
                    </span>
                    <span className='text-brandSecondary-500 font-medium text-[14px] leading-[18px]'>{item.amount.toFixed(2)} €</span>
                  </div>) :
                <span className=' text-label-md font-semibold text-neutral-500'>Nessun risultato trovato</span>}
          </div>
          {
            <div className=" flex flex-row justify-between items-end cursor-pointer w-full">
              <span onClick={() => navigate('/customer')} className=" leading-[18px] text-[13px] font-medium text-[#6237fb] hover:text-[#bfaeff]">
                Vai alla sezione Clienti
              </span>
              <div
                onClick={() => navigate('/customer')}
                className=" bg-[#6237fb] hover:bg-[#bfaeff] cursor-pointer flex flex-row items-center justify-center rounded-full size-6 text-[13px] leading-4"
              >
                <ArrowRightIcon size={"16px"} color={'white'} />
              </div>
            </div>
          }
        </div>}
        <div className=' rounded-xl shadow-xl border border-[#6237FB] p-4 flex items-center justify-center'>
          {paymentState.findAllPaymentStatus === 'loading' ? <SpinnerComponent /> :
            <div className='w-full h-[360px] flex flex-col gap-4'>
              <span className="text-[#170264] font-bold leading-[18px] text-lg">Totale Ricavi</span>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart title='Totale ricavi' width={1200} height={250} data={payments}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip contentStyle={{ color: '#6237fb' }} />
                  <Legend />
                  <Line type="monotone" dataKey="Totale ricavi" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          }
        </div>
        <div className=' rounded-xl shadow-xl border border-[#6237FB] p-4 flex items-center justify-center flex-col-reverse  '>
          <div className=' w-[50%]'>
            <SelectComponent menuPlacement='top' noOptionsMessage='Nessun cliente selezionabile' onChange={(value) => setSelectedCustomer(value)} value={selectedCustomer} placeholder={'Scegli un cliente'} options={customerOptions} />
          </div>
          <div className='w-full h-[360px] flex flex-col gap-4'>
            <span className="text-[#170264] font-bold leading-[18px] text-lg">Ricavi per Cliente</span>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart title='Totale ricavi per cliente' data={paymentsByCustomer}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip contentStyle={{ color: '#6237fb' }} />
                <Legend />
                <Line type="monotone" dataKey="Totale ricavi" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/*  <div className=' rounded-xl shadow-xl border border-[#6237FB] p-4 flex items-center justify-center'>
          {paymentState.findAllPaymentStatus === 'loading' ? <SpinnerComponent /> :
            <div className='w-full h-[300px]'>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart title='Costi reali' width={1200} height={250} data={realCosts}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip contentStyle={{ color: '#6237fb' }} />
                  <Legend />
                  <Line type="monotone" dataKey="Costo reale" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          }
        </div> */}
      </div>
      <ModalComponent width='medium' open={modalOpen} handleClose={() => setModalOpen(false)} >
        <div className=' p-5 bg-white shadow-xl rounded-xl flex flex-col gap-5 w-full' >
          <span className=' text-heading-xs text-neutral-600 font-semibold'>Richiedi pagamento</span>
          <SelectComponent noOptionsMessage='Nessun cliente' onChange={(e) => setNamespace(e!)} value={namespace} placeholder={'Scegli un cliente'} options={customerOptions} />
          <div className={"flex gap-3 items-center justify-center"}>
            <BillowButton
              label={"Annulla"}
              onClick={() => {
                setModalOpen(false)
              }}
              variant={"outline"}
              iconPosition="off"
              fullWidth
            />
            <BillowButton
              label={'Crea'}
              onClick={() => {
                setModalOpen(false)
                setConfirm(true)
              }}
              variant={"solid"}
              iconPosition="off"
              fullWidth
              disabled={
                !namespace.toString().trim()
              }
            />
          </div>
        </div>
      </ModalComponent>
    </LayoutComponent>
  )
}
